import React, { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { formatPercent } from 'i18n/format'
import { trackOnboarding } from '../../../utils/analytics'
import type { OnboardingSeriesMeta } from '../../../redux/actions/onboardingVideosAction'

interface Props {
  textbook: OnboardingSeriesMeta['textbook']
  /** Total characters the series covers — quoted in the copy ("your first 150"). */
  targetWordCount: number
  /**
   * 'full' makes the pitch on Lesson 1, where the buying decision actually
   * happens; 'compact' keeps the link reachable for the rest of the series
   * without re-selling a book the learner has already passed on.
   */
  variant: 'full' | 'compact'
}

const ArrowIcon: React.FC = () => (
  <svg viewBox='0 0 24 24' width='14' height='14' aria-hidden='true'>
    <path
      d='M7 17 17 7M9 7h8v8'
      fill='none'
      stroke='currentColor'
      strokeWidth='2.2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

/**
 * The optional paper companion to the video series, shown once in the footer
 * under the lesson. Deliberately quiet: the videos stand on their own, so this
 * is framed as an offer rather than a requirement.
 *
 * Renders nothing without a cover + purchase link, so the partnership can be
 * switched off entirely from the backend curriculum.
 *
 * Instrumented as a pair — `onboarding_textbook_offer_viewed` on mount and
 * `onboarding_textbook_offer_clicked` on either link — so PostHog can report a
 * click-through rate for the partnership, not just a raw click count. Both
 * carry `variant`, so the full card and the compact line can be compared.
 */
const TextbookCallout: React.FC<Props> = ({ textbook, targetWordCount, variant }) => {
  const { t } = useTranslation('landing')
  const { title, coverUrl, purchaseUrl, discountPercent = 0 } = textbook
  const offerLive = Boolean(coverUrl && purchaseUrl && discountPercent > 0)
  // "15%" in English, "15 %" in French.
  const percent = formatPercent(discountPercent / 100, { maximumFractionDigits: 2 })

  useEffect(() => {
    if (!offerLive) return
    trackOnboarding('onboarding_textbook_offer_viewed', {
      flow: 'onboarding_videos',
      textbook: title,
      discount_percent: discountPercent,
      variant,
    })
  }, [offerLive, title, discountPercent, variant])

  if (!offerLive) return null

  /** `placement` separates cover-art clicks from button and inline-line clicks. */
  const handleClick = (placement: 'cover' | 'button' | 'line') => () =>
    trackOnboarding('onboarding_textbook_offer_clicked', {
      flow: 'onboarding_videos',
      textbook: title,
      discount_percent: discountPercent,
      variant,
      placement,
    })

  if (variant === 'compact') {
    return (
      <p className='obv-bookline'>
        <img className='obv-bookline__thumb' src={coverUrl} alt='' loading='lazy' />
        <span>
          {t('textbook.compactLead', { title })}{' '}
          <a
            className='obv-bookline__link'
            href={purchaseUrl}
            target='_blank'
            rel='noopener noreferrer'
            onClick={handleClick('line')}
          >
            {t('textbook.compactLink', { percent })}
            <ArrowIcon />
          </a>
        </span>
      </p>
    )
  }

  return (
    <aside className='obv-book' aria-labelledby='obv-book-title'>
      <a
        className='obv-book__cover'
        href={purchaseUrl}
        target='_blank'
        rel='noopener noreferrer'
        onClick={handleClick('cover')}
        tabIndex={-1}
        aria-hidden='true'
      >
        <span className='obv-book__pages' />
        <img className='obv-book__art' src={coverUrl} alt='' loading='lazy' />
        <span className='obv-book__spine' />
        <span className='obv-book__gloss' />
      </a>

      <div className='obv-book__body'>
        <div className='obv-book__text'>
          <h3 id='obv-book-title' className='obv-book__title'>
            {title}
            {textbook.titleZh && (
              <span className='obv-book__title-zh' lang='zh'>
                {textbook.titleZh}
              </span>
            )}
          </h3>
          <p className='obv-book__pub'>{textbook.publisher}</p>

          <p className='obv-book__copy'>
            <Trans
              t={t}
              i18nKey='textbook.copy'
              values={{ count: targetWordCount, percent }}
              components={{ strong: <strong /> }}
            />
          </p>
        </div>

        <a
          className='obv-book__cta'
          href={purchaseUrl}
          target='_blank'
          rel='noopener noreferrer'
          onClick={handleClick('button')}
        >
          {t('textbook.cta', { percent })}
          <ArrowIcon />
        </a>
      </div>
    </aside>
  )
}

export default TextbookCallout
